import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { MEDIA_SERVER_URL } from "../../../constants/constants";
import { changeFeedEngagement, GetAllfeedCommentById, sendFeedRose } from "../../../apis/feed";
import "./UserFeedModal.css";

const metrics = [
  { key: "comments", label: "Comments", field: "commentCount", add: "Add Comment" },
  { key: "views", label: "Views", field: "viewCount", add: "Add Views" },
  { key: "shares", label: "Shared", field: "shareCount", add: "Add Share" },
  { key: "likes", label: "Likes", field: "likeCount", add: "Add Like" },
  { key: "favorites", label: "Favorites", field: "favouriteCount", add: "Add Favorites" },
];
const mediaUrl = (value) => !value ? "" : /^https?:\/\//i.test(value) ? value : `${MEDIA_SERVER_URL}/${value.replace(/^\/+/, "")}`;
const mediaFor = (post) => (post.NestedPicturePosts || post.nestedPosts || []).map((item) => ({
  url: mediaUrl(item.img_url || item.video_url || item.video || item.thumb_url),
  video: Boolean(item.video_url || item.video),
})).filter((item) => item.url);

function Dialog({ title, children, onClose, busy, wide = false }) {
  const panel = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    panel.current?.focus();
    return () => previous?.isConnected && previous.focus();
  }, []);
  const keyDown = (event) => {
    event.stopPropagation();
    if (event.key === "Escape" && !busy) onClose();
    if (event.key !== "Tab") return;
    const items = [...panel.current.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')];
    const first = items[0], last = items[items.length - 1];
    if (!first) { event.preventDefault(); return; }
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) { last.focus(); event.preventDefault(); }
    else if (!event.shiftKey && (document.activeElement === last || document.activeElement === panel.current)) { first.focus(); event.preventDefault(); }
  };
  return createPortal(<div className="user-feed-dialog-backdrop" onKeyDown={keyDown}>
    <section ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title}
      className={`user-feed-dialog${wide ? " wide" : ""}`}>
      <header><h2>{title}</h2><button className="user-feed-close" onClick={onClose} disabled={busy} aria-label={`Close ${title}`}>×</button></header>
      {children}
    </section>
  </div>, document.body);
}

function CountDialog({ target, onClose, onSaved }) {
  const [count, setCount] = useState("");
  const [commentText, setCommentText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const title = `${target.remove ? "Delete" : "Add"} ${target.metric.label}`;
  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const quantity = Number(count);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 1000) { setError("Enter a whole number between 1 and 1000."); return; }
    setBusy(true); setError("");
    try {
      const result = await changeFeedEngagement(target.post.id, target.metric.key, quantity, target.remove, commentText.trim());
      if (!result.success) throw new Error(result.message || "Unable to update this post.");
      onSaved(target.post.id, result.stats, result.message);
    } catch (err) { setError(err.response?.data?.message || err.message); setBusy(false); }
  };
  return <Dialog title={title} onClose={onClose} busy={busy}>
    <p>Post {target.post.id} · Current {target.metric.label.toLowerCase()}: {target.post[target.metric.field] || 0}</p>
    <form onSubmit={submit}>
      <label htmlFor="feed-engagement-count">Number to {target.remove ? "delete" : "add"}</label>
      <input id="feed-engagement-count" type="number" min="1" max="1000" step="1" required disabled={busy} value={count} onChange={(event) => setCount(event.target.value)} />
      {target.metric.key === "comments" && !target.remove && <>
        <label htmlFor="feed-comment-text">Comment text (optional)</label>
        <textarea id="feed-comment-text" maxLength={2000} value={commentText} disabled={busy} onChange={(event) => setCommentText(event.target.value)} placeholder="Leave blank to use default comments" />
      </>}
      {target.metric.key === "comments" && target.remove && <p>Deletes the oldest top-level comments and their replies. Comments containing gifts are protected.</p>}
      {error && <p className="user-feed-error" role="alert">{error}</p>}
      <footer><button onClick={onClose} type="button" className="secondary" disabled={busy}>Cancel</button><button className={target.remove ? "delete" : "add"} disabled={busy}>{busy ? "Saving…" : title}</button></footer>
    </form>
  </Dialog>;
}

function GiftDialog({ post, onClose, onSaved }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentId, setCommentId] = useState("");
  const [senderId, setSenderId] = useState("");
  const [diamonds, setDiamonds] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let current = true;
    GetAllfeedCommentById(post.id).then((result) => {
      if (!current) return;
      const list = Object.values(result.data || {}).flat();
      setComments(list); setCommentId(list[0] ? String(list[0].id) : "");
    }).catch(() => { if (current) setError("Unable to load comments for this post."); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [post.id]);
  const submit = async (event) => {
    event.preventDefault();
    if (busy || !commentId) return;
    setBusy(true); setError("");
    try {
      const result = await sendFeedRose(post.id, { commentId: Number(commentId), senderId: Number(senderId), diamonds: Number(diamonds) });
      if (!result.success) throw new Error(result.message || "Unable to send the rose.");
      onSaved(post.id, result.stats, result.message);
    } catch (err) { setError(err.response?.data?.message || err.message); setBusy(false); }
  };
  return <Dialog title="Gift Section — Feed Roses" onClose={onClose} busy={busy}>
    <p>Post {post.id}. Feed gifts are roses sent to comments. Diamonds are deducted from the sending account and become claimable by the selected comment’s author.</p>
    {loading ? <p role="status">Loading comments…</p> : !comments.length ? <p>Add a comment to this post before sending a rose.</p> : <form onSubmit={submit}>
      <label htmlFor="feed-gift-comment">Comment recipient</label>
      <select id="feed-gift-comment" value={commentId} disabled={busy} onChange={(event) => setCommentId(event.target.value)}>
        {comments.map((comment) => <option key={comment.id} value={comment.id}>#{comment.id} · {comment.user?.username || comment.User?.username || `User ${comment.user_id}`} · {String(comment.comment_data || "").slice(0, 70)}</option>)}
      </select>
      <label htmlFor="feed-gift-sender">Sending account ID</label>
      <input id="feed-gift-sender" type="number" required min="1" step="1" disabled={busy} value={senderId} onChange={(event) => setSenderId(event.target.value)} />
      <label htmlFor="feed-gift-amount">Diamonds</label>
      <input id="feed-gift-amount" type="number" required min="1" max="1000000" step="1" disabled={busy} value={diamonds} onChange={(event) => setDiamonds(event.target.value)} />
      <footer><button type="button" className="secondary" disabled={busy} onClick={onClose}>Cancel</button><button className="add" disabled={busy}>{busy ? "Sending…" : "Send Rose"}</button></footer>
    </form>}
    {error && <p className="user-feed-error" role="alert">{error}</p>}
  </Dialog>;
}

export default function UserFeedModal({ posts, userId, loading, error, onClose, onRetry, onPostUpdated, admin }) {
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [page, setPage] = useState(1);
  const [jumpPage, setJumpPage] = useState("");
  const [action, setAction] = useState(null);
  const [giftPost, setGiftPost] = useState(null);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState("");
  const allowed = admin?.role === "superadmin" || admin?.permissions?.Users?.["View feed"]?.selected === true;
  const nestedOpen = Boolean(action || giftPost || preview);
  const list = useMemo(() => {
    const query = search.trim().toLowerCase();
    const end = endDate ? new Date(endDate) : null;
    if (end) end.setHours(23, 59, 59, 999);
    return (posts || []).filter((post) => {
      const date = new Date(post.createdAt);
      return (!startDate || date >= startDate) && (!end || date <= end) &&
        (!query || `${post.id} ${post.description || ""} ${post.post_topic || ""}`.toLowerCase().includes(query));
    });
  }, [posts, search, startDate, endDate]);
  const pages = Math.max(1, Math.ceil(list.length / 5));
  useEffect(() => { setPage(1); }, [search, startDate, endDate]);
  useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);
  useEffect(() => {
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = overflow; };
  }, []);
  const saved = (postId, stats, text) => {
    onPostUpdated(postId, stats); setAction(null); setGiftPost(null); setMessage(text);
  };
  const openAction = (post, metric, remove) => { setMessage(""); setAction({ post, metric, remove }); };
  const countCell = (post, metric) => <td key={metric.key}><div className="user-feed-count">
    <span>{Number(post[metric.field] || 0).toLocaleString()}</span>
    <button className="add" disabled={!allowed} onClick={() => openAction(post, metric, false)}>{metric.add}</button>
    <button className="delete" disabled={!allowed} onClick={() => openAction(post, metric, true)}>Delete</button>
  </div></td>;
  const currentMedia = preview ? preview.media[preview.index] : null;
  return <>
    {createPortal(<div className="user-feed-modal-backdrop" onKeyDown={(event) => { if (!nestedOpen && event.key === "Escape") onClose(); }}>
      <section className="user-feed-modal" role="dialog" aria-modal="true" aria-label="User Feeds" aria-hidden={nestedOpen || undefined}>
        <header><h2>User Feeds — User {userId}</h2><button className="user-feed-close" onClick={onClose} aria-label="Close User Feeds">×</button></header>
        <input className="user-feed-search" aria-label="Search user feed posts" placeholder="Search by post ID or description" value={search} onChange={(event) => setSearch(event.target.value)} />
        <div className="user-feed-filters">
          <DatePicker selected={startDate} onChange={setStartDate} selectsStart startDate={startDate} endDate={endDate} maxDate={endDate || new Date()} placeholderText="Start Date" dateFormat="dd:MM:yyyy" />
          <span>to</span>
          <DatePicker selected={endDate} onChange={setEndDate} selectsEnd startDate={startDate} endDate={endDate} minDate={startDate} maxDate={new Date()} placeholderText="End Date" dateFormat="dd:MM:yyyy" />
          <button onClick={() => { setStartDate(null); setEndDate(null); setSearch(""); }}>Clean</button>
        </div>
        {message && <p className="user-feed-success" role="status">{message}</p>}
        {loading ? <p role="status">Loading user feeds…</p> : error ? <p className="user-feed-error" role="alert">{error} <button onClick={onRetry}>Retry</button></p> : <>
          <div className="user-feed-table-scroll"><table>
            <thead><tr><th>ID</th><th>Date</th><th>Feed</th><th>Comments</th><th>Views</th><th>Shared</th><th>Diamonds</th><th>Likes</th><th>Favorites</th><th>Gift Section</th></tr></thead>
            <tbody>{list.slice((page - 1) * 5, page * 5).map((post) => {
              const media = mediaFor(post);
              return <tr key={post.id}>
                <td>{post.id}</td><td>{post.createdAt ? new Date(post.createdAt).toLocaleString() : "—"}</td>
                <td>{media.length ? <button className="user-feed-thumbnail" onClick={() => setPreview({ media, index: 0 })} aria-label={`View feed post ${post.id} images`}><img src={media[0].url} alt="Feed thumbnail" /><small>{media.length} {media.length === 1 ? "image" : "images"}</small></button> : "No media"}</td>
                {metrics.slice(0, 3).map((metric) => countCell(post, metric))}
                <td>{Number(post.diamondValue || 0).toLocaleString()}</td>
                {metrics.slice(3).map((metric) => countCell(post, metric))}
                <td><button className="add" disabled={!allowed} onClick={() => { setMessage(""); setGiftPost(post); }}>Send Gift</button></td>
              </tr>;
            })}{!list.length && <tr><td colSpan={10}>No feed posts found for this user.</td></tr>}</tbody>
          </table></div>
          <div className="user-feed-pagination">
            <button disabled={page === 1} onClick={() => setPage(1)}>First</button><button disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
            {Array.from({ length: Math.min(4, pages) }, (_, i) => Math.min(Math.max(1, page - 1), Math.max(1, pages - 3)) + i).map((number) => <button key={number} className={page === number ? "selected" : ""} aria-current={page === number ? "page" : undefined} onClick={() => setPage(number)}>{number}</button>)}
            <button disabled={page === pages} onClick={() => setPage(page + 1)}>Next</button><button disabled={page === pages} onClick={() => setPage(pages)}>Last</button>
          </div>
          <form className="user-feed-jump" onSubmit={(event) => { event.preventDefault(); const number = Number(jumpPage); if (Number.isInteger(number) && number >= 1 && number <= pages) { setPage(number); setJumpPage(""); } }}>
            <input aria-label="Feed page number" type="number" min="1" max={pages} value={jumpPage} onChange={(event) => setJumpPage(event.target.value)} placeholder="Page No" /><button>Go</button>
          </form>
        </>}
      </section>
    </div>, document.body)}
    {action && <CountDialog key={`${action.post.id}-${action.metric.key}-${action.remove}`} target={action} onClose={() => setAction(null)} onSaved={saved} />}
    {giftPost && <GiftDialog post={giftPost} onClose={() => setGiftPost(null)} onSaved={saved} />}
    {currentMedia && <Dialog title="Feed Media" wide onClose={() => setPreview(null)}>
      <div className="user-feed-media">{currentMedia.video ? <video src={currentMedia.url} controls /> : <img src={currentMedia.url} alt={`Feed image ${preview.index + 1}`} />}</div>
      <footer><button disabled={preview.index === 0} onClick={() => setPreview({ ...preview, index: preview.index - 1 })}>Previous</button><span>{preview.index + 1} / {preview.media.length}</span><button disabled={preview.index === preview.media.length - 1} onClick={() => setPreview({ ...preview, index: preview.index + 1 })}>Next</button></footer>
    </Dialog>}
  </>;
}
